/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { RootStore } from 'src/core/stores/RootStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

export const modelTraversalHelpers = (rootStore: RootStore) => ({
    /**
     * Gets the model with the given id from the
     * app with the given id and the version with
     * the given id (if it were a conversation app).
     *
     * @param modelId The id of the model to get.
     * @param appId The id of the app to get the model from.
     * @param versionId The id of the version to get the model from.
     * @returns The model with the given id. The model can either
     * be an entity or intent.
     */
    getModelById: (modelId: string, appId: string, versionId?: string) => {
        const appVersionStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);

        return appVersionStore?.intentStoreHouse.findById(modelId) || appVersionStore?.entityStoreHouse.findById(modelId);
    },

    /**
     * Gets the model from the current application and potentially
     * from current application version.
     */
    getModelByIdFromCurrentApp: (modelId: string) => {
        const appVersionStore = rootStore.currentLuisAppStore?.currentAppVersionStore;

        return appVersionStore?.intentStoreHouse.findById(modelId) || appVersionStore?.entityStoreHouse.findById(modelId);
    },

    /**
     * Gets the model with the given id and parent id
     *
     * 1. Gets ExtractorModelStore using the given parent id
     * 2. Check if returned model schema is equal to model id
     * 3. traverse schema to get model to be returned
     *
     * @param parentModelId The id of the parent model to get.
     * @param modelId The id of the model to get.
     * @param appId The id of the app to get the model from.
     * @param versionId The id of the version to get the model from.
     * @returns The model with the given id. The model can either
     * be an entity or intent.
     */
    getExtractorSchema: (parentModelId: string, modelId: string, appId: string, versionId?: string) => {
        const appVersionStore = rootStore.luisAppStoreHouse.findById(appId)?.appVersionStoreHouse.findById(versionId);

        if (parentModelId === modelId || !parentModelId) {
            return appVersionStore?.intentStoreHouse.findById(modelId) || appVersionStore?.entityStoreHouse.findById(modelId);
        }
        const parentModelStore =
            appVersionStore?.intentStoreHouse.findById(parentModelId) || appVersionStore?.entityStoreHouse.findById(parentModelId);

        return (<SimpleEntityStore>parentModelStore).getChildById(modelId);
    }
});
